import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DBTable from '../handlers/DatabaseHandler';
import { sendnsee } from '../handlers/ChatbotHandler';
import { renderMarkdown } from '../handlers/MarkdownHandler';
import { useTranslation } from 'react-i18next';
import { handleMarkdownShortcut } from '../utils/textareaMarkdownShortcuts';
import LoadingSpinner from '../components/blocks/LoadingSpinner';
import PageTutorialGuide from '../components/tutorial/PageTutorialGuide';
import TutorialInlineButton from '../components/tutorial/TutorialInlineButton';
import { resolveTutorialGuideSteps } from '../components/tutorial/tutorialGuideSteps';
import { usePageTutorial } from '../components/tutorial/usePageTutorial';
import ShadedSelect from '../components/ui/ShadedSelect';
import { formControlClass, formControlTextareaClass } from '../components/ui/formUiClasses';
import { COLLECTION } from '../config/mongoCollections.js';

const MAP_ZOOM_MIN = 0.5;
const MAP_ZOOM_MAX = 2;
const MAP_ZOOM_STEP = 0.1;

/** getBoundingClientRect is after CSS transforms; SVG uses layout (unscaled) space inside the layer. */
function getLayerViewportToContentScale(layerEl) {
  const r = layerEl.getBoundingClientRect();
  const w = layerEl.offsetWidth;
  const h = layerEl.offsetHeight;
  if (r.width < 1e-6 || r.height < 1e-6) return { sx: 1, sy: 1 };
  return { sx: w / r.width, sy: h / r.height };
}

function TrajectoryMap() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, isAuthenticated } = useAuth();
  const forceTutorial = searchParams.get('tutorial') === '1';
  const { guideOpen, openGuide, closeGuide } = usePageTutorial('trajectory', { forceOpen: forceTutorial });
  const sidebarTutorialRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newEvent, setNewEvent] = useState({
    type: '',
    name: '',
    year: new Date().getFullYear(),
    description: ''
  });
  const [newConnection, setNewConnection] = useState({
    from: '',
    to: '',
    type: 'influenced',
    description: ''
  });
  const [showCustomTypePopup, setShowCustomTypePopup] = useState(false);
  const [showManageTypesPopup, setShowManageTypesPopup] = useState(false);
  const [showEditEventPopup, setShowEditEventPopup] = useState(false);
  const [showEditConnectionPopup, setShowEditConnectionPopup] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [showConnectionsPopup, setShowConnectionsPopup] = useState(false);
  const [selectedEventConnections, setSelectedEventConnections] = useState([]);
  const [customTypeInput, setCustomTypeInput] = useState('');
  const [currentYear] = useState(new Date().getFullYear());
  const [eventTypes, setEventTypes] = useState(['academic', 'extracurricular', 'personal']);
  const [yearError, setYearError] = useState('');
  const [isYearValid, setIsYearValid] = useState(true);
  const [isEventValid, setIsEventValid] = useState(false);
  const [isConnectionValid, setIsConnectionValid] = useState(false);
  const eventRefs = useRef(new Map());
  const containerRef = useRef(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [dragState, setDragState] = useState({
    isDragging: false,
    sourceEvent: null,
    currentPosition: { x: 0, y: 0 }
  });
  const [initialEventState, setInitialEventState] = useState(null);
  const [initialConnectionState, setInitialConnectionState] = useState(null);
  const [draggedType, setDraggedType] = useState(null);
  const [dragOverType, setDragOverType] = useState(null);
  const [temporaryConnection, setTemporaryConnection] = useState(null);
  const [showAnalysisPopup, setShowAnalysisPopup] = useState(false);
  const [showPastAnalysesPopup, setShowPastAnalysesPopup] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [pastAnalyses, setPastAnalyses] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [showConnections, setShowConnections] = useState(true);
  const [openedFromPastAnalyses, setOpenedFromPastAnalyses] = useState(false);
  const [isBottomPanelOpen, setIsBottomPanelOpen] = useState(false);
  const [dragStartY, setDragStartY] = useState(null);
  const [panelHeight, setPanelHeight] = useState(80);
  const [isCreatingChat, setCreatingChat] = useState(false);
  const [data, setData] = useState({
    // ... existing code ...
  });
  const [headerDragState, setHeaderDragState] = useState({
    isDragging: false,
    startX: 0,
    scrollLeft: 0
  });
  const [connectionResolutionPromise, setConnectionResolutionPromise] = useState(null);
  const mapExportRef = useRef(null);
  const mapScrollRef = useRef(null);
  const trajectoryTutorialAnchors = {
    sidebar: sidebarTutorialRef,
    map: mapScrollRef
  };
  const mapContentLayerRef = useRef(null);
  const scrollRafRef = useRef(null);
  const [mapZoom, setMapZoom] = useState(1);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [showZoomHelpPopover, setShowZoomHelpPopover] = useState(false);
  const zoomHelpPopoverRef = useRef(null);
  const zoomHelpRootRef = useRef(null);
  const [zoomPanelOffset, setZoomPanelOffset] = useState({ x: 0, y: 0 });
  const zoomPanelDragRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0
  });
  const { t, i18n } = useTranslation();
  const trajectoryGuideSteps = resolveTutorialGuideSteps('trajectory', t);

  const clampMapZoom = (z) =>
    Math.min(MAP_ZOOM_MAX, Math.max(MAP_ZOOM_MIN, Math.round(z * 10) / 10));

  const setMapContentLayerRef = useCallback((el) => {
    mapContentLayerRef.current = el;
    if (el) {
      requestAnimationFrame(() => setForceUpdate((f) => f + 1));
    }
  }, []);

  const handleMapScroll = useCallback(() => {
    if (scrollRafRef.current != null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      setForceUpdate((f) => f + 1);
    });
  }, []);

  // Function to refresh AuthContext data
  const refreshAuthContext = async () => {
    try {
      const authResponse = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (authResponse.ok) {
        const authData = await authResponse.json();
        // console.log('TrajectoryMap - Refreshed AuthContext data:', authData);
        return authData;
      }
    } catch (error) {
      console.error('TrajectoryMap - Error refreshing AuthContext:', error);
    }
    return null;
  };

  // Debug eventTypes changes
  useEffect(() => {
    // console.log('TrajectoryMap - eventTypes state changed to:', eventTypes);
  }, [eventTypes]);

  const handleMouseMove = (e) => {
    if (dragState.isDragging) {
      const layerEl = mapContentLayerRef.current;
      if (!layerEl) return;
      const layerRect = layerEl.getBoundingClientRect();
      const { sx, sy } = getLayerViewportToContentScale(layerEl);
      setDragState(prev => ({
        ...prev,
        currentPosition: {
          x: (e.clientX - layerRect.left) * sx,
          y: (e.clientY - layerRect.top) * sy
        }
      }));
    }
  };

  const handleMouseUp = (e) => {
    if (dragState.isDragging) {
      const layerEl = mapContentLayerRef.current;
      if (!layerEl) {
        setDragState({
          isDragging: false,
          sourceEvent: null,
          currentPosition: { x: 0, y: 0 }
        });
        return;
      }
      const layerRect = layerEl.getBoundingClientRect();
      const { sx, sy } = getLayerViewportToContentScale(layerEl);
      const mouseX = (e.clientX - layerRect.left) * sx;
      const mouseY = (e.clientY - layerRect.top) * sy;

      // Find the event card under the mouse
      const targetEvent = Array.from(eventRefs.current.values()).find(eventEl => {
        const rect = eventEl.getBoundingClientRect();
        const relativeX = (rect.left - layerRect.left) * sx;
        const relativeY = (rect.top - layerRect.top) * sy;
        const rw = rect.width * sx;
        const rh = rect.height * sy;
        return (
          mouseX >= relativeX &&
          mouseX <= relativeX + rw &&
          mouseY >= relativeY &&
          mouseY <= relativeY + rh
        );
      });

      if (targetEvent) {
        // Find the event data for the target element
        const targetEventData = events.find(event =>
          eventRefs.current.get(event.id) === targetEvent
        );

        if (targetEventData && targetEventData.id !== dragState.sourceEvent.id) {
          // Create a new temporary connection
          const newConnection = {
            id: `connection_${Date.now()}`,
            from: dragState.sourceEvent.id,
            to: targetEventData.id,
            type: 'influenced',
            description: ''
          };

          // Add the connection to the list temporarily
          setConnections(prev => [...prev, newConnection]);
          setTemporaryConnection(newConnection);

          // Set the connection for editing
          setSelectedConnection(newConnection);
          setShowEditConnectionPopup(true);
        }
      }

      setDragState({
        isDragging: false,
        sourceEvent: null,
        currentPosition: { x: 0, y: 0 }
      });
    }
  };

  const handleTouchStart = (e) => {
    // Only prevent default if we're actually starting to drag
    if (dragStartY === null) {
      e.preventDefault();
    }
    setDragStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (dragStartY === null) return;

    // Only prevent default if we're actually dragging
    e.preventDefault();
    const deltaY = dragStartY - e.touches[0].clientY;
    const newHeight = Math.max(80, Math.min(window.innerHeight * 0.8, panelHeight + deltaY));
    setPanelHeight(newHeight);
    setDragStartY(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    setDragStartY(null);
    // Snap to open or closed state
    if (panelHeight > window.innerHeight * 0.4) {
      setPanelHeight(window.innerHeight * 0.8);
      setIsBottomPanelOpen(true);
    } else {
      setPanelHeight(80);
      setIsBottomPanelOpen(false);
    }
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
    setDragStartY(clientY);
    setIsBottomPanelOpen(true);
  };

  const handleDragMove = (e) => {
    e.preventDefault();
    if (dragStartY === null) return;

    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
    const deltaY = dragStartY - clientY;
    const newHeight = Math.max(80, Math.min(window.innerHeight * 0.8, panelHeight + deltaY));
    setPanelHeight(newHeight);
    setDragStartY(clientY);
  };

  const handleDragEnd = () => {
    setDragStartY(null);
    // Snap to open or closed state
    if (panelHeight > window.innerHeight * 0.4) {
      setPanelHeight(window.innerHeight * 0.8);
      setIsBottomPanelOpen(true);
    } else {
      setPanelHeight(40); // Reduced from 60 to 40
      setIsBottomPanelOpen(false);
    }
  };

  const handleHeaderMouseDown = (e) => {
    e.preventDefault(); // Prevent default behavior
    const scrollContainer = containerRef.current.querySelector('.flex-1.overflow-auto');
    if (!scrollContainer) return;

    setHeaderDragState({
      isDragging: true,
      startX: e.pageX - scrollContainer.offsetLeft,
      scrollLeft: scrollContainer.scrollLeft
    });
  };

  const handleHeaderMouseMove = (e) => {
    if (!headerDragState.isDragging) return;
    e.preventDefault(); // Prevent default behavior

    const scrollContainer = containerRef.current.querySelector('.flex-1.overflow-auto');
    if (!scrollContainer) return;

    const x = e.pageX - scrollContainer.offsetLeft;
    const walk = (x - headerDragState.startX) * 2; // Multiply by 2 for faster scrolling
    scrollContainer.scrollLeft = headerDragState.scrollLeft - walk;
  };

  const handleHeaderMouseUp = (e) => {
    e.preventDefault(); // Prevent default behavior
    setHeaderDragState({
      isDragging: false,
      startX: 0,
      scrollLeft: 0
    });
  };

  // Add effect to handle initial connection rendering
  useEffect(() => {
    if (isInitialRender && events.length > 0 && connections.length > 0) {
      // Small delay to ensure DOM elements are mounted
      const timer = setTimeout(() => {
        setForceUpdate(prev => prev + 1);
        setIsInitialRender(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [events, connections, isInitialRender]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setForceUpdate((f) => f + 1));
    return () => cancelAnimationFrame(id);
  }, [mapZoom]);

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      // Hide connections if screen is too narrow
      setShowConnections(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('mousemove', handleHeaderMouseMove);
    window.addEventListener('mouseup', handleHeaderMouseUp);

    // Add touch event listeners with passive: false
    const dragHandle = document.querySelector('.drag-handle');
    if (dragHandle) {
      dragHandle.addEventListener('touchstart', handleTouchStart, { passive: false });
      dragHandle.addEventListener('touchmove', handleTouchMove, { passive: false });
      dragHandle.addEventListener('touchend', handleTouchEnd, { passive: false });
    }

    // Initial check
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('mousemove', handleHeaderMouseMove);
      window.removeEventListener('mouseup', handleHeaderMouseUp);

      // Remove touch event listeners
      if (dragHandle) {
        dragHandle.removeEventListener('touchstart', handleTouchStart);
        dragHandle.removeEventListener('touchmove', handleTouchMove);
        dragHandle.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [dragState.isDragging, events, dragStartY, headerDragState.isDragging]);

  useEffect(() => {
    // console.log('TrajectoryMap - useEffect triggered with currentUser:', currentUser);
    if (currentUser) {
      // console.log('TrajectoryMap - currentUser has User_ID:', currentUser.User_ID);
      // console.log('TrajectoryMap - currentUser event_types:', currentUser.event_types);
      loadTrajectoryData();
    }
  }, [currentUser]);

  const userTable = new DBTable(
    COLLECTION.users,
    "User_ID",
    {
      User_ID: "",
      First_Name: "",
      Last_Name: "",
      Nickname: "",
      Email_Address: "",
      Tel: "",
      User_Role: "",
      direct_marketing: false,
      email_list: false,
      card_id: "",
      ability_6d: [1.5, 1.5, 1.5, 1.5, 1.5, 1.5],
      trajectory_events: [],
      trajectory_connections: [],
      trajectory_analyses: [],
      event_types: ['academic', 'extracurricular', 'personal']
    }
  );

  // Validate event form
  useEffect(() => {
    const year = parseInt(newEvent.year);
    const isValid =
      newEvent.name.trim() !== '' &&
      newEvent.type !== '' &&
      newEvent.year !== '' &&
      !isNaN(year) &&
      validateYear(year) === '';
    setIsEventValid(isValid);
  }, [newEvent]);

  // Validate connection form
  useEffect(() => {
    const isValid =
      newConnection.from !== '' &&
      newConnection.to !== '' &&
      newConnection.type !== '' &&
      newConnection.from !== newConnection.to; // Prevent reflexive relations
    setIsConnectionValid(isValid);
  }, [newConnection]);

  const loadTrajectoryData = async () => {
    try {
      // console.log('TrajectoryMap - loadTrajectoryData called with currentUser:', currentUser);
      if (!currentUser || !currentUser.User_ID) {
        console.error("No user data found");
        navigate('/login');
        return;
      }

      // console.log('TrajectoryMap - Fetching complete user data for:', currentUser.User_ID);

      // Fetch complete user data from database
      const userData = await userTable.handleRead({ User_ID: currentUser.User_ID }, false);

      if (userData) {
        // console.log('TrajectoryMap - Complete user data:', userData);
        // console.log('TrajectoryMap - Setting events:', userData.trajectory_events);
        // console.log('TrajectoryMap - Setting connections:', userData.trajectory_connections);
        // console.log('TrajectoryMap - Setting past analyses:', userData.trajectory_analyses);

        setEvents(userData.trajectory_events || []);
        setConnections(userData.trajectory_connections || []);
        setPastAnalyses(userData.trajectory_analyses || []);

        // Set event types from user data, preserving the order
        if (userData.event_types && userData.event_types.length > 0) {
          // console.log('TrajectoryMap - Setting event types from user data:', userData.event_types);
          // console.log('TrajectoryMap - Current AuthContext event_types:', currentUser.event_types);

          // Check if AuthContext and database have different event types
          if (currentUser.event_types && JSON.stringify(currentUser.event_types) !== JSON.stringify(userData.event_types)) {
            // console.log('TrajectoryMap - WARNING: AuthContext and database have different event types!');
            // console.log('TrajectoryMap - AuthContext:', currentUser.event_types);
            // console.log('TrajectoryMap - Database:', userData.event_types);
          }

          setEventTypes(userData.event_types);
        } else {
          // Only use default if no event types exist
          // console.log('TrajectoryMap - No event types in user data, using defaults');
          setEventTypes(['academic', 'extracurricular', 'personal']);
        }

        setIsInitialRender(true);
      } else {
        console.error("TrajectoryMap - Failed to fetch user data");
      }
    } catch (error) {
      console.error("Error loading trajectory data:", error);
      if (error.message.includes('401')) {
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateYear = (year) => {
    const currentYear = new Date().getFullYear();
    const minYear = 1900; // Reasonable minimum year
    const maxYear = currentYear + 10; // Allow up to 10 years in the future

    if (!Number.isInteger(year)) {
      return 'Year must be a whole number';
    }
    if (year < minYear) {
      return `Year must be after ${minYear}`;
    }
    if (year > maxYear) {
      return `Year cannot be more than 10 years in the future`;
    }
    return '';
  };

  const handleYearChange = (e) => {
    const value = e.target.value;
    const year = parseInt(value);

    if (value === '') {
      setNewEvent(prev => ({ ...prev, year: '' }));
      setYearError('');
      setIsYearValid(false);
      return;
    }

    if (!isNaN(year)) {
      const error = validateYear(year);
      setYearError(error);
      setIsYearValid(!error);
      setNewEvent(prev => ({ ...prev, year }));
    } else {
      setNewEvent(prev => ({ ...prev, year: value }));
      setIsYearValid(false);
    }
  };

  const handleAddEvent = async () => {
    if (!newEvent.name.trim()) return;

    const year = parseInt(newEvent.year);
    const error = validateYear(year);
    if (error) {
      setYearError(error);
      setIsYearValid(false);
      return;
    }

    try {
      if (!currentUser || !currentUser.User_ID) {
        throw new Error("No user data found");
      }

      const newEventObj = {
        id: `event_${Date.now()}`,
        name: newEvent.name.trim(),
        year: parseInt(newEvent.year),
        type: newEvent.type,
        description: newEvent.description?.trim() || '',
        timestamp: new Date().toISOString()
      };

      const updatedEvents = [...events, newEventObj];

      const response = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trajectory_events: updatedEvents
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add event');
      }

      setEvents(updatedEvents);
      setNewEvent({ type: '', name: '', year: new Date().getFullYear(), description: '' });
      setYearError('');
      setIsYearValid(true);
    } catch (error) {
      console.error("Error adding event:", error);
      alert('Failed to add event. Please try again.');
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setInitialEventState({ ...event });
    setShowEditEventPopup(true);
  };

  const handleConnectionClick = (connection) => {
    setSelectedConnection(connection);
    setInitialConnectionState({ ...connection });
    setShowEditConnectionPopup(true);
  };

  const handleAddConnection = async () => {
    if (!newConnection.from || !newConnection.to) return;

    // Prevent reflexive relations
    if (newConnection.from === newConnection.to) {
      alert('Cannot create a connection from an event to itself');
      return;
    }

    try {
      if (!currentUser || !currentUser.User_ID) {
        throw new Error("No user data found");
      }

      const newConnectionObj = {
        id: `connection_${Date.now()}`,
        from: newConnection.from,
        to: newConnection.to,
        type: newConnection.type,
        description: newConnection.description.trim()
      };

      const response = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trajectory_connections: [...connections, newConnectionObj]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add connection');
      }

      setConnections([...connections, newConnectionObj]);
      setNewConnection({
        from: '',
        to: '',
        type: 'influenced',
        description: ''
      });
    } catch (error) {
      console.error('Error adding connection:', error);
      alert('Failed to add connection. Please try again.');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const updatedEvents = events.filter(event => event.id !== eventId);

      // Also remove any connections involving this event
      const updatedConnections = connections.filter(
        conn => conn.from !== eventId && conn.to !== eventId
      );

      const response = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trajectory_events: updatedEvents,
          trajectory_connections: updatedConnections
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete event');
      }

      setEvents(updatedEvents);
      setConnections(updatedConnections);
      setShowEditEventPopup(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const handleDeleteConnection = async (connectionId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const updatedConnections = connections.filter(conn => conn.id !== connectionId);

      const response = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trajectory_connections: updatedConnections
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete connection');
      }

      setConnections(updatedConnections);

      // If this was triggered by invalid connections during analysis, resolve the promise
      if (selectedConnection?.hasMissingEvents && connectionResolutionPromise) {
        connectionResolutionPromise();
        setConnectionResolutionPromise(null);
      }

      setShowEditConnectionPopup(false);
      setSelectedConnection(null);
    } catch (error) {
      console.error('Error deleting connection:', error);
    }
  };

  const handleUpdateEvent = async () => {
    try {
      if (!currentUser || !currentUser.User_ID) {
        throw new Error("No user data found");
      }

      const updatedEvents = events.map(event =>
        event.id === selectedEvent.id ? selectedEvent : event
      );
      setEvents(updatedEvents);

      const response = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trajectory_events: updatedEvents
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update event');
      }

      setShowEditEventPopup(false);
    } catch (error) {
      console.error("Error updating event:", error);
    }
  };

  const handleUpdateConnection = async () => {
    try {
      if (!currentUser || !currentUser.User_ID) {
        throw new Error("No user data found");
      }

      // Prevent reflexive relations
      if (selectedConnection.from === selectedConnection.to) {
        alert('Cannot create a connection from an event to itself');
        return;
      }

      const updatedConnections = connections.map(connection =>
        connection.id === selectedConnection.id ? selectedConnection : connection
      );

      const response = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trajectory_connections: updatedConnections
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update connection');
      }

      setConnections(updatedConnections);

      // If this was triggered by invalid connections during analysis, resolve the promise
      if (selectedConnection?.hasMissingEvents && connectionResolutionPromise) {
        connectionResolutionPromise();
        setConnectionResolutionPromise(null);
      }

      setShowEditConnectionPopup(false);
      setSelectedConnection(null);
      setInitialConnectionState(null);
    } catch (error) {
      console.error('Error updating connection:', error);
      alert('Failed to update connection. Please try again.');
    }
  };

  const getConnectionColor = (type) => {
    switch (type) {
      case 'influenced':
        return '#3B82F6'; // blue-500
      case 'led_to':
        return '#10B981'; // emerald-500
      case 'related':
        return '#8B5CF6'; // violet-500
      default:
        return '#6B7280'; // gray-500
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'academic':
        return 'bg-sky-100 text-sky-900 dark:bg-sky-950/50 dark:text-sky-200';
      case 'extracurricular':
        return 'bg-green-100 text-green-900 dark:bg-green-950/50 dark:text-green-200';
      case 'personal':
        return 'bg-purple-100 text-purple-900 dark:bg-purple-950/50 dark:text-purple-200';
      default:
        return 'bg-surface-muted text-ink border border-edge';
    }
  };

  const getTypeBorderColor = (type) => {
    switch (type) {
      case 'academic':
        return 'border-sky-300 dark:border-sky-700';
      case 'extracurricular':
        return 'border-green-300 dark:border-green-700';
      case 'personal':
        return 'border-purple-300 dark:border-purple-700';
      default:
        return 'border-edge';
    }
  };

  const getEventPosition = (event, isStart = true, targetEvent = null) => {
    const eventElement = eventRefs.current.get(event.id);
    const layerEl = mapContentLayerRef.current;

    if (!eventElement || !layerEl) {
      return { x: 0, y: 0 };
    }

    const eventRect = eventElement.getBoundingClientRect();
    const layerRect = layerEl.getBoundingClientRect();
    const { sx, sy } = getLayerViewportToContentScale(layerEl);

    const left = (eventRect.left - layerRect.left) * sx;
    const top = (eventRect.top - layerRect.top) * sy;
    const ew = eventRect.width * sx;
    const eh = eventRect.height * sy;

    // Determine if the arrow should point downward
    let isDownward = false;
    if (targetEvent) {
      const targetElement = eventRefs.current.get(targetEvent.id);
      if (targetElement) {
        const targetRect = targetElement.getBoundingClientRect();
        isDownward = targetRect.top !== eventRect.top;
      }
    }

    let x, y;
    if (isDownward) {
      x = left + ew / 2;
      if (isStart) {
        y = top + eh;
      } else {
        y = top;
      }
    } else {
      x = isStart ? left + ew : left;
      y = top + eh / 2;
    }

    return { x, y };
  };

  const renderConnections = () => {
    const layerEl = mapContentLayerRef.current;
    if (!layerEl) return null;

    return connections.map((connection) => {
      const fromEvent = events.find(e => e.id === connection.from);
      const toEvent = events.find(e => e.id === connection.to);

      if (!fromEvent || !toEvent) return null;

      const fromElement = eventRefs.current.get(fromEvent.id);
      const toElement = eventRefs.current.get(toEvent.id);

      if (!fromElement || !toElement) return null;

      const fromRect = fromElement.getBoundingClientRect();
      const toRect = toElement.getBoundingClientRect();
      const layerRect = layerEl.getBoundingClientRect();
      const { sx, sy } = getLayerViewportToContentScale(layerEl);

      const fromX = (fromRect.left - layerRect.left) * sx;
      const fromY = (fromRect.top - layerRect.top) * sy;
      const toX = (toRect.left - layerRect.left) * sx;
      const toY = (toRect.top - layerRect.top) * sy;
      const fromW = fromRect.width * sx;
      const fromH = fromRect.height * sy;
      const toW = toRect.width * sx;
      const toH = toRect.height * sy;

      const dx = toX - fromX;
      const dy = toY - fromY;

      let startX, startY, endX, endY;

      if (Math.abs(dx) > Math.abs(dy)) {
        startX = dx > 0 ? fromX + fromW : fromX;
        startY = fromY + fromH / 2;
        endX = dx > 0 ? toX : toX + toW;
        endY = toY + toH / 2;
      } else {
        startX = fromX + fromW / 2;
        startY = dy > 0 ? fromY + fromH : fromY;
        endX = toX + toW / 2;
        endY = dy > 0 ? toY : toY + toH;
      }

      // Calculate control points for the curved arrow
      const midX = (startX + endX) / 2;
      const horizontalDistance = endX - startX;

      // Adjust curve height based on horizontal distance
      const minCurveHeight = 30;
      const maxCurveHeight = 100;
      const curveHeight = Math.min(
        maxCurveHeight,
        Math.max(minCurveHeight, Math.abs(horizontalDistance) * 0.3)
      );

      const controlY = Math.min(startY, endY) - curveHeight;

      // Calculate the middle point of the curve
      const t = 0.5; // Parameter for the quadratic Bezier curve
      const midPoint = {
        x: Math.pow(1 - t, 2) * startX + 2 * (1 - t) * t * midX + Math.pow(t, 2) * endX,
        y: Math.pow(1 - t, 2) * startY + 2 * (1 - t) * t * controlY + Math.pow(t, 2) * endY
      };

      const connectionColor = getConnectionColor(connection.type);
      const symbol = connection.type.charAt(0).toUpperCase();

      return (
        <svg
          key={connection.id}
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            zIndex: 4,  // Lower than popup's z-index of 50
            position: 'absolute',  // Keep scrolling with container
            pointerEvents: 'none'
          }}
        >
          <defs>
            <marker
              id="arrowhead-influenced"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill="#3B82F6"
              />
            </marker>
            <marker
              id="arrowhead-led_to"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill="#10B981"
              />
            </marker>
            <marker
              id="arrowhead-related"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill="#8B5CF6"
              />
            </marker>
            <marker
              id="arrowhead-default"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill="#6B7280"
              />
            </marker>
          </defs>
          <path
            d={`M ${startX} ${startY} Q ${midX} ${controlY}, ${endX} ${endY}`}
            stroke={connectionColor}
            strokeWidth="2"
            fill="none"
            markerEnd={`url(#arrowhead-${connection.type || 'default'})`}
          />
          {/* Clickable circle with symbol */}
          <g
            onClick={(e) => {
              e.stopPropagation();
              setSelectedConnection(connection);
              setShowEditConnectionPopup(true);
            }}
            className="pointer-events-auto cursor-pointer"
          >
            <circle
              cx={midPoint.x}
              cy={midPoint.y}
              r="12"
              fill="white"
              stroke={connectionColor}
              strokeWidth="2"
            />
            <text
              x={midPoint.x}
              y={midPoint.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={connectionColor}
              className="font-semibold"
            >
              {symbol}
            </text>
          </g>
        </svg>
      );
    });
  };

  const handleCustomTypeSubmit = async () => {
    if (customTypeInput.trim()) {
      const newType = customTypeInput.trim().toLowerCase();
      if (!eventTypes.includes(newType)) {
        const newTypes = [...eventTypes, newType];

        try {
          const response = await fetch('/api/auth/me', {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              event_types: newTypes
            })
          });

          if (!response.ok) {
            throw new Error('Failed to update event types');
          }

          setEventTypes(newTypes);
          setNewEvent(prev => ({ ...prev, type: newType }));
          setCustomTypeInput('');
          setShowCustomTypePopup(false);

          // Refresh AuthContext data to ensure synchronization
          await refreshAuthContext();

          // Reopen bottom panel after adding new type
          setIsBottomPanelOpen(true);
        } catch (error) {
          console.error('Error updating event types:', error);
          alert('Failed to add new event type. Please try again.');
        }
      }
    }
  };

  const handleDeleteType = async (typeToDelete) => {
    // Don't allow deletion if there are events using this type
    const eventsUsingType = events.filter(event => event.type === typeToDelete);
    if (eventsUsingType.length > 0) {
      alert(`Cannot delete this type as it is being used by ${eventsUsingType.length} event(s).`);
      return;
    }

    const newTypes = eventTypes.filter(type => type !== typeToDelete);

    try {
      const response = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event_types: newTypes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update event types');
      }

      setEventTypes(newTypes);
      if (newEvent.type === typeToDelete) {
        setNewEvent({ ...newEvent, type: '' });
      }

      // Refresh AuthContext data to ensure synchronization
      await refreshAuthContext();
    } catch (error) {
      console.error('Error updating event types:', error);
      alert('Failed to update event types. Please try again.');
    }
  };

  // Calculate the earliest year for the timeline
  const getEarliestYear = () => {
    if (events.length === 0) {
      return currentYear - 5;
    }
    const earliestEventYear = Math.min(...events.map(event => event.year));
    return Math.min(earliestEventYear, currentYear - 5);
  };

  // Generate years array for the timeline
  const getYears = () => {
    const earliestYear = getEarliestYear();
    const years = [];
    for (let year = earliestYear; year <= currentYear + 5; year++) {
      years.push(year);
    }
    return years;
  };

  const sortEventsTopologically = (events, connections) => {
    // Create a map of event dependencies
    const dependencies = new Map();
    const eventMap = new Map(events.map(event => [event.id, event]));

    // Initialize dependencies
    events.forEach(event => {
      dependencies.set(event.id, new Set());
    });

    // Add dependencies based on connections
    connections.forEach(connection => {
      // Only add dependency if both events exist in the current group
      if (eventMap.has(connection.from) && eventMap.has(connection.to)) {
        dependencies.get(connection.to).add(connection.from);
      }
    });

    // Perform topological sort
    const sortedEvents = [];
    const visited = new Set();
    const temp = new Set();

    const visit = (eventId) => {
      if (temp.has(eventId)) {
        // Cycle detected, but we'll handle it gracefully
        return;
      }
      if (visited.has(eventId)) {
        return;
      }

      temp.add(eventId);
      dependencies.get(eventId).forEach(depId => {
        visit(depId);
      });
      temp.delete(eventId);
      visited.add(eventId);
      sortedEvents.push(eventMap.get(eventId));
    };

    // Visit all events
    events.forEach(event => {
      if (!visited.has(event.id)) {
        visit(event.id);
      }
    });

    return sortedEvents;
  };

  const getEventConnections = (eventId) => {
    return connections.filter(conn => conn.from === eventId || conn.to === eventId);
  };

  const handleConnectionsClick = (event) => {
    const eventConnections = getEventConnections(event.id);
    setSelectedEvent(event);
    setSelectedEventConnections(eventConnections);
    setShowConnectionsPopup(true);
  };

  const handleTypeDragStart = (e, type) => {
    setDraggedType(type);
    e.dataTransfer.effectAllowed = 'move';
    // Add a class to the dragged element
    e.target.classList.add('dragging');
  };

  const handleTypeDragOver = (e, type) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (type !== draggedType) {
      setDragOverType(type);
    }
  };

  const handleTypeDragLeave = (e, type) => {
    // Only reset if we're leaving the current dragOverType
    if (type === dragOverType) {
      setDragOverType(null);
    }
  };

  const handleTypeDrop = async (e, targetType) => {
    e.preventDefault();
    if (!draggedType || draggedType === targetType) return;

    const draggedIndex = eventTypes.indexOf(draggedType);
    const targetIndex = eventTypes.indexOf(targetType);

    // console.log('TrajectoryMap - handleTypeDrop - draggedType:', draggedType, 'targetType:', targetType);
    // console.log('TrajectoryMap - handleTypeDrop - draggedIndex:', draggedIndex, 'targetIndex:', targetIndex);

    const newTypes = [...eventTypes];
    newTypes.splice(draggedIndex, 1);
    newTypes.splice(targetIndex, 0, draggedType);

    // console.log('TrajectoryMap - handleTypeDrop - newTypes:', newTypes);

    try {
      const response = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event_types: newTypes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update event types');
      }

      // console.log('TrajectoryMap - handleTypeDrop - Successfully updated event types in database');
      setEventTypes(newTypes);

      // Refresh AuthContext data to ensure synchronization
      await refreshAuthContext();

      setDraggedType(null);
      setDragOverType(null);
      // Remove the dragging class
      e.target.classList.remove('dragging');
      // Force update to ensure arrows are rerendered
      setForceUpdate(prev => prev + 1);
    } catch (error) {
      console.error('Error updating event types:', error);
      alert('Failed to update event types. Please try again.');
    }
  };

  const handleConnectionDragStart = (event) => {
    const eventElement = eventRefs.current.get(event.id);
    const layerEl = mapContentLayerRef.current;
    if (!eventElement || !layerEl) return;

    const rect = eventElement.getBoundingClientRect();
    const layerRect = layerEl.getBoundingClientRect();
    const { sx, sy } = getLayerViewportToContentScale(layerEl);

    const left = (rect.left - layerRect.left) * sx;
    const top = (rect.top - layerRect.top) * sy;
    const rw = rect.width * sx;
    const rh = rect.height * sy;

    const startX = left + rw;
    const startY = top + rh / 2;

    setDragState({
      isDragging: true,
      sourceEvent: event,
      currentPosition: {
        x: startX,
        y: startY
      }
    });
  };

  const renderDragLine = () => {
    if (!dragState.isDragging) return null;

    const sourceEvent = dragState.sourceEvent;
    const sourcePos = getEventPosition(sourceEvent, true);
    const currentPos = dragState.currentPosition;

    const dx = currentPos.x - sourcePos.x;
    const dy = currentPos.y - sourcePos.y;

    const sourceElement = eventRefs.current.get(sourceEvent.id);
    const layerEl = mapContentLayerRef.current;
    if (!sourceElement || !layerEl) return null;

    const sourceRect = sourceElement.getBoundingClientRect();
    const layerRect = layerEl.getBoundingClientRect();
    const { sx, sy } = getLayerViewportToContentScale(layerEl);

    const relLeft = (sourceRect.left - layerRect.left) * sx;
    const relTop = (sourceRect.top - layerRect.top) * sy;
    const sw = sourceRect.width * sx;
    const sh = sourceRect.height * sy;

    let startX, startY;
    if (Math.abs(dx) > Math.abs(dy)) {
      startX = dx > 0 ? relLeft + sw : relLeft;
      startY = relTop + sh / 2;
    } else {
      startX = relLeft + sw / 2;
      startY = dy > 0 ? relTop + sh : relTop;
    }

    return (
      <svg
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
        style={{ zIndex: 4 }}
      >
        <path
          d={`M ${startX} ${startY} L ${currentPos.x} ${currentPos.y}`}
          stroke={getConnectionColor('influenced')}
          strokeWidth="2"
          fill="none"
        />
        <circle
          cx={currentPos.x}
          cy={currentPos.y}
          r="5"
          fill={getConnectionColor('influenced')}
        />
      </svg>
    );
  };

  const hasEventChanged = () => {
    if (!selectedEvent || !initialEventState) return false;
    return (
      selectedEvent.name !== initialEventState.name ||
      selectedEvent.year !== initialEventState.year ||
      selectedEvent.type !== initialEventState.type ||
      selectedEvent.description !== initialEventState.description
    );
  };

  const hasConnectionChanged = () => {
    if (!selectedConnection || !initialConnectionState) return false;
    return (
      selectedConnection.from !== initialConnectionState.from ||
      selectedConnection.to !== initialConnectionState.to ||
      selectedConnection.type !== initialConnectionState.type ||
      selectedConnection.description !== initialConnectionState.description
    );
  };

  const handleCloseEditConnectionPopup = () => {
    if (temporaryConnection) {
      // Remove the temporary connection if it exists
      setConnections(prev => prev.filter(conn => conn.id !== temporaryConnection.id));
      setTemporaryConnection(null);
    }

    // If this was triggered by invalid connections during analysis, resolve the promise
    if (selectedConnection?.hasMissingEvents && connectionResolutionPromise) {
      connectionResolutionPromise();
      setConnectionResolutionPromise(null);
    }

    setShowEditConnectionPopup(false);
    setSelectedConnection(null);
    setInitialConnectionState(null);
  };

  const formatDataForAnalysis = () => {
    return new Promise((resolve, reject) => {
      // Sort events by year
      const sortedEvents = [...events].sort((a, b) => a.year - b.year);

      // Format events
      const formattedEvents = sortedEvents.map(event => ({
        name: event.name,
        year: event.year,
        type: event.type,
        description: event.description
      }));

      // Format connections
      const formattedConnections = connections.map(conn => {
        const fromEvent = events.find(e => e.id === conn.from);
        const toEvent = events.find(e => e.id === conn.to);

        // Check if either event is not found
        if (!fromEvent || !toEvent) {
          // Create a new promise for connection resolution
          const resolutionPromise = new Promise((resolveConnection) => {
            setConnectionResolutionPromise(() => resolveConnection);
          });

          // Set the connection for editing with a flag indicating missing events
          setSelectedConnection({
            ...conn,
            hasMissingEvents: true
          });
          setShowEditConnectionPopup(true);

          // Reject with the resolution promise
          reject({ type: 'Invalid connections found', resolutionPromise });
          return null;
        }

        return {
          from: fromEvent.name,
          to: toEvent.name,
          type: conn.type,
          description: conn.description
        };
      }).filter(Boolean);

      resolve({
        events: formattedEvents,
        connections: formattedConnections
      });
    });
  };

  const handleAnalyzeTrajectory = async () => {
    setIsAnalyzing(true);
    try {
      // First check for invalid connections
      const formattedData = await formatDataForAnalysis().catch(async (error) => {
        if (error.type === 'Invalid connections found') {
          setIsAnalyzing(false);
          // Wait for the user to resolve the connections
          await error.resolutionPromise;
          // Close the popup
          setShowEditConnectionPopup(false);
          setSelectedConnection(null);
          setInitialConnectionState(null);
          // After resolution, get the formatted data again
          return formatDataForAnalysis();
        }
        throw error;
      });

      // Only proceed with analysis if we have valid data
      const prompt = `Analyze this student's trajectory and suggest potential career paths based on their experiences and connections. 
      Events: ${JSON.stringify(formattedData.events)}
      Connections: ${JSON.stringify(formattedData.connections)}
      Please provide a detailed analysis focusing on:
      1. Key strengths and interests shown through your experiences
      2. Potential career paths that align with your trajectory
      3. Recommendations for your future development
      4. Any notable patterns or themes in your journey
      
      Please write the analysis using second-person pronouns (you, your) to make it more personal and engaging.
      Skip any formalities or introductions - start directly with the analysis. Mark titles with **title**. No colon after titles.`;

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch('/api/chatbot/generate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: prompt })
      });

      if (!response.ok) {
        throw new Error('Failed to get analysis');
      }

      const data = await response.json();
      if (!data || !data.content) {
        throw new Error('Invalid analysis data received');
      }

      // Create new analysis
      const analysis = {
        id: `analysis_${Date.now()}`,
        timestamp: new Date().toISOString(),
        content: data.content
      };

      if (!currentUser || !currentUser.User_ID) {
        throw new Error("No user data found");
      }

      const userData = await userTable.handleRead({ User_ID: currentUser.User_ID }, false);

      if (userData) {
        const updatedAnalyses = [analysis, ...(userData.trajectory_analyses || [])];
        // console.log('Updating analyses:', updatedAnalyses); // Debug log

        const updateResponse = await fetch('/api/auth/me', {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            trajectory_analyses: updatedAnalyses
          })
        });

        if (!updateResponse.ok) {
          throw new Error('Failed to update analyses');
        }

        const updatedUser = await updateResponse.json();
        // console.log('Updated user data:', updatedUser); // Debug log

        // Update local state
        setPastAnalyses(updatedAnalyses);
        setCurrentAnalysis(analysis);
        setShowAnalysisPopup(true);
      }
    } catch (error) {
      console.error('Error analyzing trajectory:', error);
      if (error.type === 'Invalid connections found') {
        return;
      }
      alert('Failed to analyze trajectory. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Calculate number of columns based on screen width
  const getGridColumns = () => {
    if (screenWidth < 1024) return 1; // xs: single column for small screens
    if (screenWidth < 4080) return 2; // m: double column for conventional screens
    return 3; // lg and above: three columns for large screens
  };

  const calculateYearHeight = (year) => {
    let maxEvents = 0;
    eventTypes.forEach(type => {
      const yearEvents = events.filter(e => e.year === year && e.type === type);
      maxEvents = Math.max(maxEvents, yearEvents.length);
    });
    // Each event card takes about 220px height, plus 12px gap
    // Add extra padding for better spacing
    return Math.max(256, Math.ceil(maxEvents / getGridColumns()) * 232 + 32);
  };

  const handleDeleteAnalysis = async (analysisId) => {
    try {
      if (!currentUser || !currentUser.User_ID) {
        throw new Error("No user data found");
      }

      const updatedAnalyses = pastAnalyses.filter(analysis => analysis.id !== analysisId);

      const response = await fetch('/api/auth/me', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trajectory_analyses: updatedAnalyses
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete analysis');
      }

      setPastAnalyses(updatedAnalyses);
    } catch (error) {
      console.error('Error deleting analysis:', error);
      alert('Failed to delete analysis. Please try again.');
    }
  };

  const handleEventTypeChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setShowCustomTypePopup(true);
      setNewEvent(prev => ({ ...prev, type: '' }));
      setIsBottomPanelOpen(false);
    } else {
      setNewEvent(prev => ({ ...prev, type: value }));
    }
  };

  // Get language-specific chat messages
  const getChatMessages = () => {
    const currentLanguage = i18n.language || 'en';

    switch (currentLanguage) {
      case 'tc':
        return {
          initialMessage: `我想同愛迪生討論呢個分析：\n\n${currentAnalysis.content}`,
          responsePrompt: "請分析呢個軌跡同埋提供你嘅見解。"
        };
      case 'sc':
        return {
          initialMessage: `嗨！我想和爱迪生讨论这个分析：\n\n${currentAnalysis.content}`,
          responsePrompt: "请分析这个轨迹并提供你的见解。"
        };
      case 'en':
      default:
        return {
          initialMessage: `I'd like to discuss this analysis:\n\n${currentAnalysis.content}`,
          responsePrompt: "Please analyze this trajectory and provide your insights."
        };
    }
  };

  const handleMapWheelZoom = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -MAP_ZOOM_STEP : MAP_ZOOM_STEP;
    setMapZoom((z) => clampMapZoom(z + delta));
  };

  const handleZoomPanelDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    zoomPanelDragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: zoomPanelOffset.x,
      originY: zoomPanelOffset.y
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!zoomPanelDragRef.current.active) return;
      const d = zoomPanelDragRef.current;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      setZoomPanelOffset({ x: d.originX + dx, y: d.originY + dy });
    };
    const onUp = () => {
      zoomPanelDragRef.current.active = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  useEffect(() => {
    if (!showZoomHelpPopover) return;
    const onDocDown = (e) => {
      if (zoomHelpRootRef.current && zoomHelpRootRef.current.contains(e.target)) return;
      setShowZoomHelpPopover(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setShowZoomHelpPopover(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showZoomHelpPopover]);

  const handleExportTrajectoryPdf = async () => {
    const el = mapExportRef.current;
    const scrollEl = mapScrollRef.current;
    if (!el || !scrollEl) return;

    const prevScrollTop = scrollEl.scrollTop;
    const prevScrollLeft = scrollEl.scrollLeft;

    setIsExportingPdf(true);
    try {
      scrollEl.scrollTop = 0;
      scrollEl.scrollLeft = 0;
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      await new Promise((r) => setTimeout(r, 50));

      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      const stripShadowsAndRaiseHeader = (clonedDoc) => {
        const root = clonedDoc.getElementById('trajectory-map-export-root');
        if (!root) return;
        const header = clonedDoc.querySelector('[data-timeline-header]');
        const layer = clonedDoc.querySelector('[data-map-content-layer]');
        if (header) {
          header.style.position = 'relative';
          header.style.zIndex = '100';
          header.style.backgroundColor = '#ffffff';
          header.style.boxShadow = 'none';
        }
        if (layer) {
          layer.style.position = 'relative';
          layer.style.zIndex = '1';
        }
        root.querySelectorAll('*').forEach((node) => {
          if (node.style) {
            node.style.boxShadow = 'none';
            node.style.filter = 'none';
          }
        });
      };

      /**
       * PDF rasterization uses a cloned DOM only — do not change live mapZoom.
       * Applying max zoom-out on the clone matches prior “export zoomed out” behavior
       * without disturbing viewport ↔ layout ratios used for arrows.
       * Omitting windowWidth/windowHeight avoids html2canvas stretching the iframe viewport
       * (very wide × tall) which distorted horizontal layout.
       */
      const applyPdfExportLayoutToClone = (clonedRoot) => {
        if (!clonedRoot) return;
        clonedRoot.style.transform = `scale(${MAP_ZOOM_MIN})`;
        clonedRoot.style.transformOrigin = 'top left';
        clonedRoot.style.width = `${100 / MAP_ZOOM_MIN}%`;
      };

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc, clonedElement) => {
          const clonedRoot =
            clonedElement?.id === 'trajectory-map-export-root'
              ? clonedElement
              : clonedDoc.getElementById('trajectory-map-export-root');
          applyPdfExportLayoutToClone(clonedRoot);
          stripShadowsAndRaiseHeader(clonedDoc);
        }
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'l' : 'p',
        unit: 'mm',
        format: 'a4'
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save('trajectory-map.pdf');
    } catch (err) {
      console.error(err);
      alert(t('trajectory.exportPdfError'));
    } finally {
      scrollEl.scrollTop = prevScrollTop;
      scrollEl.scrollLeft = prevScrollLeft;
      setIsExportingPdf(false);
    }
  };

  return (
    <div className={`flex ${screenWidth >= 1024 ? 'h-[calc(100vh-4rem)] overflow-hidden' : 'h-screen overflow-hidden'}`}>
      {/* Show loading state while auth is being checked */}
      {isLoading && (
        <div className="flex items-center justify-center h-full w-full">
          <LoadingSpinner size="large" text={t('common.loading')} />
        </div>
      )}

      {/* Show login prompt if not authenticated */}
      {!isLoading && !isAuthenticated && (
        <div className="flex items-center justify-center h-full w-full">
          <div className="text-lg">Please log in to access your trajectory map.</div>
        </div>
      )}

      {/* Show trajectory interface if authenticated */}
      {!isLoading && isAuthenticated && (
        <>
          {/* Left sidebar - only visible on large screens */}
          {screenWidth >= 1024 && (
            <div ref={sidebarTutorialRef} className="w-80 bg-surface border-r border-edge p-4 flex flex-col overflow-y-auto">
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4 gap-2">
                  <h2 className="text-lg font-semibold">{t('trajectory.addEvent')}</h2>
                  <div className="flex items-center gap-2 shrink-0">
                  <TutorialInlineButton
                    onClick={openGuide}
                    unstyled
                    className="px-2.5 py-1.5 text-xs font-semibold rounded-full border border-edge-strong text-ink-muted bg-surface hover:bg-surface-muted hover:text-link-hover hover:border-link/40 transition-colors"
                  />
                  <button
                    onClick={() => setShowManageTypesPopup(true)}
                    className="text-sm text-ink-subtle hover:text-ink"
                    title={t('trajectory.manageEventTypes')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                  </div>
                </div>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder={t('trajectory.eventName')}
                    value={newEvent.name}
                    onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                    onKeyDown={(e) =>
                      handleMarkdownShortcut(e, newEvent.name, (next) =>
                        setNewEvent({ ...newEvent, name: next })
                      )
                    }
                    className={formControlClass}
                  />
                  <div>
                    <input
                      type="number"
                      placeholder={t('trajectory.year')}
                      value={newEvent.year}
                      onChange={handleYearChange}
                      className={`${formControlClass}${yearError ? ' !border-red-500' : ''}`}
                      min="1900"
                      max={new Date().getFullYear() + 10}
                      step="1"
                    />
                    {yearError && (
                      <p className="text-red-500 text-sm mt-1">{yearError}</p>
                    )}
                  </div>
                  <ShadedSelect
                    value={newEvent.type}
                    onChange={(v) => handleEventTypeChange({ target: { value: v } })}
                    className="w-full"
                    options={[
                      { value: '', label: t('trajectory.selectEventType') },
                      ...eventTypes.map((type) => ({
                        value: type,
                        label: type.charAt(0).toUpperCase() + type.slice(1),
                      })),
                      { value: 'custom', label: t('trajectory.addNew') },
                    ]}
                  />
                  <textarea
                    placeholder={t('trajectory.eventDescription')}
                    value={newEvent.description || ''}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    onKeyDown={(e) =>
                      handleMarkdownShortcut(e, newEvent.description || '', (next) =>
                        setNewEvent({ ...newEvent, description: next })
                      )
                    }
                    className={formControlTextareaClass}
                  />
                  <button
                    onClick={handleAddEvent}
                    disabled={!isEventValid}
                    className={`w-full p-2 rounded-full ${isEventValid
                      ? 'bg-primary text-primary-foreground hover:bg-primary-hover'
                      : 'bg-surface-muted text-ink-subtle border border-edge cursor-not-allowed'
                      }`}
                  >
                    {t('trajectory.addEvent')}
                  </button>
                </div>
              </div>

              {/* Custom Type Popup */}
              {showCustomTypePopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-surface p-6 rounded-lg shadow-lg w-96">
                    <h3 className="text-lg font-semibold mb-4">{t('trajectory.addNewEventType')}</h3>
                    <input
                      type="text"
                      placeholder={t('trajectory.enterNewEventType')}
                      value={customTypeInput}
                      onChange={(e) => setCustomTypeInput(e.target.value)}
                      onKeyDown={(e) =>
                        handleMarkdownShortcut(e, customTypeInput, (next) => setCustomTypeInput(next))
                      }
                      className={`${formControlClass} mb-4`}
                    />
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => {
                          setShowCustomTypePopup(false);
                          setCustomTypeInput('');
                        }}
                        className="px-4 py-2 rounded-full border border-edge-strong bg-surface text-ink hover:bg-surface-muted hover:border-edge"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        onClick={handleCustomTypeSubmit}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                      >
                        {t('common.addNew')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Manage Types Popup */}
              {showManageTypesPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 50 }}>
                  <div className="bg-surface p-6 rounded-lg shadow-lg w-96">
                    <h3 className="text-lg font-semibold mb-4">{t('trajectory.manageEventTypes')}</h3>
                    <div className="space-y-2 mb-4">
                      {eventTypes.map(type => {
                        const isDragged = type === draggedType;
                        const isDragOver = type === dragOverType;
                        const draggedIndex = eventTypes.indexOf(draggedType);
                        const currentIndex = eventTypes.indexOf(type);

                        // Calculate shift direction only when actively dragging over
                        const shouldShift = dragOverType && !isDragged && !isDragOver;
                        const shiftDirection = shouldShift ?
                          (currentIndex > draggedIndex && currentIndex <= eventTypes.indexOf(dragOverType) ? -1 :
                            currentIndex < draggedIndex && currentIndex >= eventTypes.indexOf(dragOverType) ? 1 : 0) : 0;

                        return (
                          <div
                            key={type}
                            draggable
                            onDragStart={(e) => handleTypeDragStart(e, type)}
                            onDragOver={(e) => handleTypeDragOver(e, type)}
                            onDragLeave={(e) => handleTypeDragLeave(e, type)}
                            onDrop={(e) => handleTypeDrop(e, type)}
                            className={`flex items-center justify-between p-2 rounded cursor-move transition-all duration-200 ${isDragged ? 'opacity-50 bg-surface-muted' :
                              isDragOver ? 'bg-accent/25' :
                                'bg-canvas hover:bg-surface-muted'
                              }`}
                            style={{
                              transform: isDragged ? 'scale(1.05)' :
                                shouldShift ? `translateY(${shiftDirection * 40}px)` : 'none',
                              transition: 'transform 0.5s ease-out, background-color 0.5s ease-out'
                            }}
                          >
                            <span className="capitalize">{type}</span>
                            <button
                              onClick={() => handleDeleteType(type)}
                              className="text-red-500 hover:text-red-700"
                              title={t('trajectory.deleteType')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={() => {
                          setShowManageTypesPopup(false);
                          // Reopen bottom panel after closing manage types popup
                          setIsBottomPanelOpen(true);
                        }}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                      >
                        {t('common.close')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <h2 className="text-lg font-semibold mb-4">{t('trajectory.addConnection')}</h2>
                <div className="space-y-4">
                  <ShadedSelect
                    value={newConnection.from}
                    onChange={(v) => setNewConnection({ ...newConnection, from: v })}
                    className="w-full"
                    options={[
                      { value: '', label: t('trajectory.fromEvent') },
                      ...events
                        .filter((event) => event.id !== newConnection.to)
                        .map((event) => ({ value: event.id, label: event.name })),
                    ]}
                  />
                  <ShadedSelect
                    value={newConnection.to}
                    onChange={(v) => setNewConnection({ ...newConnection, to: v })}
                    className="w-full"
                    options={[
                      { value: '', label: t('trajectory.toEvent') },
                      ...events
                        .filter((event) => event.id !== newConnection.from)
                        .map((event) => ({ value: event.id, label: event.name })),
                    ]}
                  />
                  <ShadedSelect
                    value={newConnection.type}
                    onChange={(v) => setNewConnection({ ...newConnection, type: v })}
                    className="w-full"
                    options={[
                      { value: '', label: t('trajectory.selectConnectionType') },
                      { value: 'influenced', label: t('trajectory.influenced') },
                      { value: 'led_to', label: t('trajectory.ledTo') },
                      { value: 'related', label: t('trajectory.related') },
                    ]}
                  />
                  <textarea
                    placeholder={t('trajectory.connectionDescription')}
                    value={newConnection.description || ''}
                    onChange={(e) => setNewConnection({ ...newConnection, description: e.target.value })}
                    onKeyDown={(e) =>
                      handleMarkdownShortcut(e, newConnection.description || '', (next) =>
                        setNewConnection({ ...newConnection, description: next })
                      )
                    }
                    className={formControlTextareaClass}
                  />
                  <button
                    onClick={handleAddConnection}
                    disabled={!isConnectionValid}
                    className={`w-full p-2 rounded-full ${isConnectionValid
                      ? 'bg-primary text-primary-foreground hover:bg-primary-hover'
                      : 'bg-surface-muted text-ink-subtle border border-edge cursor-not-allowed'
                      }`}
                  >
                    {t('common.addConnection')}
                  </button>
                </div>
              </div>

              {/* Analysis buttons */}
              <div className="mt-6 space-y-2">
                <button
                  onClick={() => {
                    handleAnalyzeTrajectory();
                    setIsBottomPanelOpen(false);
                  }}
                  disabled={isAnalyzing}
                  className={`w-full p-2 rounded-full ${isAnalyzing
                    ? 'bg-surface-muted text-ink-subtle border border-edge cursor-not-allowed'
                    : 'bg-primary text-primary-foreground hover:bg-primary-hover'
                    }`}
                >
                  {isAnalyzing ? t('common.loading') : t('trajectory.trajectoryAnalysis')}
                </button>
                <button
                  onClick={() => {
                    setShowPastAnalysesPopup(true);
                    setIsBottomPanelOpen(false);
                  }}
                  className="w-full p-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                >
                  {t('trajectory.pastAnalyses')}
                </button>
              </div>

              {/* Copyright statement */}
              <div className="mt-auto pt-4 text-center text-xs text-ink-subtle border-t border-edge">
                © {new Date().getFullYear()} EDVise. All rights reserved.
              </div>
            </div>
          )}

          {/* Main content area */}
          <div className="flex-1 flex flex-col overflow-hidden relative" ref={containerRef} style={{ height: 'calc(100vh - 4rem)' }}>
            {/* Add arrowhead marker definitions */}
            <svg style={{ position: 'absolute', width: 0, height: 0, zIndex: 1 }}>
              <defs>
                <marker
                  id="arrowhead-influenced"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#3B82F6"
                  />
                </marker>
                <marker
                  id="arrowhead-led_to"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#10B981"
                  />
                </marker>
                <marker
                  id="arrowhead-related"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#8B5CF6"
                  />
                </marker>
                <marker
                  id="arrowhead-default"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#6B7280"
                  />
                </marker>
              </defs>
            </svg>

            <div
              className="absolute z-20 flex max-w-[min(100vw-1rem,24rem)] flex-wrap items-center gap-1 rounded-lg border border-edge bg-surface/95 px-1.5 py-1 shadow-sm backdrop-blur-sm"
              style={{ top: `${8 + zoomPanelOffset.y}px`, right: `${8 - zoomPanelOffset.x}px` }}
            >
              <div
                className="flex flex-wrap items-center gap-0.5"
                role="group"
                aria-label={t('trajectory.zoomControlsGroupLabel')}
              >
                <button
                  type="button"
                  onClick={() => setMapZoom((z) => clampMapZoom(z - MAP_ZOOM_STEP))}
                  disabled={mapZoom <= MAP_ZOOM_MIN}
                  className="rounded-full px-2 py-1 text-sm font-medium text-ink-muted hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-40"
                  title={t('trajectory.zoomOutTooltip')}
                  aria-label={t('trajectory.zoomOut')}
                >
                  −
                </button>
                <span
                  className="min-w-[2.75rem] cursor-help text-center text-xs text-ink-muted tabular-nums"
                  title={t('trajectory.zoomPercentTooltip')}
                >
                  {t('trajectory.zoomPercent', { value: Math.round(mapZoom * 100) })}
                </span>
                <button
                  type="button"
                  onClick={() => setMapZoom((z) => clampMapZoom(z + MAP_ZOOM_STEP))}
                  disabled={mapZoom >= MAP_ZOOM_MAX}
                  className="rounded-full px-2 py-1 text-sm font-medium text-ink-muted hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-40"
                  title={t('trajectory.zoomInTooltip')}
                  aria-label={t('trajectory.zoomIn')}
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => setMapZoom(1)}
                  className="rounded-full px-2 py-1 text-xs text-ink-muted hover:bg-surface-muted"
                  title={t('trajectory.resetZoomTooltip')}
                >
                  {t('trajectory.resetZoom')}
                </button>
                <div ref={zoomHelpRootRef} className="relative" data-zoom-help-root>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowZoomHelpPopover((v) => !v);
                    }}
                    className={`rounded p-1 hover:bg-surface-muted ${showZoomHelpPopover ? 'bg-surface-muted text-ink' : 'text-ink-subtle hover:text-ink'}`}
                    title={t('trajectory.zoomScrollInstructions')}
                    aria-label={t('trajectory.zoomScrollHelpAria')}
                    aria-expanded={showZoomHelpPopover}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                  {showZoomHelpPopover && (
                    <div
                      ref={zoomHelpPopoverRef}
                      role="tooltip"
                      className="absolute right-0 top-full z-30 mt-1 w-[min(36rem,calc(100vw-2rem))] rounded-md border border-edge bg-surface p-3 text-left text-xs leading-relaxed text-ink-muted shadow-lg"
                    >
                      <p className="font-medium text-ink">{t('trajectory.zoomHelpPopoverTitle')}</p>
                      <p className="mt-1.5">{t('trajectory.zoomScrollInstructions')}</p>
                      <p className="mt-2 text-[11px] text-ink-subtle">{t('trajectory.zoomHint')}</p>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onMouseDown={handleZoomPanelDragStart}
                className="mx-0.5 flex min-h-[1.25rem] cursor-grab touch-none flex-col items-center justify-center self-stretch rounded bg-transparent px-0.5 py-0.5 text-ink-subtle hover:bg-surface-muted hover:text-ink-muted active:cursor-grabbing"
                title={t('trajectory.zoomPanelDragHandleTitle')}
                aria-label={t('trajectory.zoomPanelDragHandleAria')}
              >
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <circle cx="9" cy="8" r="1.5" />
                  <circle cx="15" cy="8" r="1.5" />
                  <circle cx="9" cy="12" r="1.5" />
                  <circle cx="15" cy="12" r="1.5" />
                  <circle cx="9" cy="16" r="1.5" />
                  <circle cx="15" cy="16" r="1.5" />
                </svg>
              </button>
              <button
                type="button"
                onClick={handleExportTrajectoryPdf}
                disabled={isExportingPdf}
                className="rounded-full px-2 py-1 text-xs font-medium text-link hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-50"
                title={t('trajectory.exportPdfTooltip')}
              >
                {isExportingPdf ? t('trajectory.exportingPdf') : t('trajectory.exportPdf')}
              </button>
            </div>

            {/* Scrollable container for both header and content */}
            <div
              ref={mapScrollRef}
              className="flex-1 overflow-auto scrollbar-auto scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              style={{ height: 'calc(100vh - 4rem)' }}
              onWheel={handleMapWheelZoom}
              onScroll={handleMapScroll}
            >
              <div
                id="trajectory-map-export-root"
                ref={mapExportRef}
                style={{
                  transform: `scale(${mapZoom})`,
                  transformOrigin: 'top left',
                  width: `${100 / mapZoom}%`
                }}
              >
              {/* Timeline header (event types) */}
              <div
                data-timeline-header
                className="flex w-full bg-surface z-10 border-b sticky top-0 cursor-grab active:cursor-grabbing select-none"
                style={{ minWidth: 'max-content', width: '100%' }}
                onMouseDown={handleHeaderMouseDown}
                onMouseMove={handleHeaderMouseMove}
                onMouseUp={handleHeaderMouseUp}
                onMouseLeave={handleHeaderMouseUp}
              >
                {/* Empty cell for year legend alignment */}
                <div className="w-16" />
                <div className="flex flex-1">
                  {/* console.log('TrajectoryMap - Rendering header with eventTypes:', eventTypes) */}
                  {eventTypes.map((type, index) => (
                    <div
                      key={type}
                      className="flex-1 py-2 border-b"
                      style={{
                        minWidth: `${300 * getGridColumns()}px`,
                        width: '100%'
                      }}
                    >
                      <h3 className="text-lg font-semibold capitalize text-center">{type}</h3>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeline content area */}
              <div
                ref={setMapContentLayerRef}
                data-map-content-layer
                className="relative"
                style={{ minWidth: 'max-content', width: '100%', minHeight: 'max-content' }}
              >
                {/* Render connections only if screen is wide enough */}
                {showConnections && renderConnections()}
                {/* Always show dragline regardless of screen size */}
                {renderDragLine()}

                {/* Timeline grid: each year is a row, each event type is a column */}
                <div className="flex flex-col w-full" style={{ zIndex: 2 }}>
                  {getYears().map((year, rowIdx) => {
                    const yearHeight = calculateYearHeight(year);
                    const rowBgClass = rowIdx % 2 === 0 ? 'bg-surface' : 'bg-canvas';
                    return (
                      <div key={year} className={`flex w-full border-b border-edge ${rowBgClass}`} style={{ minHeight: `${yearHeight}px` }}>
                        {/* Year legend cell - sticky */}
                        <div
                          className={`w-16 flex items-center justify-center border-r border-edge sticky left-0 ${rowBgClass}`}
                          style={{ minHeight: `${yearHeight}px`, zIndex: 5, padding: '0.5rem' }}
                        >
                          <span className="text-ink-subtle font-medium">{year}</span>
                        </div>
                        {/* Event type columns for this year */}
                        <div className="flex w-full">
                          {/* console.log('TrajectoryMap - Rendering grid columns with eventTypes:', eventTypes) */}
                          {eventTypes.map((type, index) => {
                            const yearEvents = sortEventsTopologically(
                              events.filter(e => e.year === year && e.type === type),
                              connections
                            );
                            return (
                              <div
                                key={type}
                                className={`w-full px-2 py-2 grid gap-x-6 gap-y-3 justify-items-center min-w-[280px] ${index < eventTypes.length - 1 ? 'border-r' : ''}`}
                                style={{
                                  minHeight: `${yearHeight}px`,
                                  minWidth: `${300 * getGridColumns()}px`,
                                  width: '100%',
                                  display: 'grid',
                                  gridTemplateColumns: `repeat(${getGridColumns()}, 1fr)`,
                                  gridAutoFlow: 'row'
                                }}
                              >
                                {yearEvents.map((event) => (
                                  <div
                                    key={event.id}
                                    ref={el => eventRefs.current.set(event.id, el)}
                                    onClick={() => handleEventClick(event)}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      handleConnectionDragStart(event);
                                    }}
                                    className={`bg-surface rounded-lg shadow-md p-4 border ${getTypeBorderColor(
                                      event.type
                                    )} group cursor-pointer hover:shadow-lg transition-shadow w-[280px] max-h-[220px] flex flex-col relative select-none`}
                                    style={{ boxSizing: 'border-box', zIndex: 4 }}
                                  >
                                    {/* Event card content */}
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <h3 className="font-semibold text-ink">{event.name}</h3>
                                        <p className="text-sm text-ink-subtle">{event.year}</p>
                                        {event.description && (
                                          <p className="mt-2 text-sm text-ink-muted line-clamp-2">{event.description}</p>
                                        )}
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteEvent(event.id);
                                        }}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700"
                                        title={t('trajectory.deleteEvent')}
                                      >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleConnectionsClick(event);
                                      }}
                                      className="mt-2 w-full py-2 bg-surface-muted border border-edge hover:bg-surface hover:border-edge-strong rounded-full text-sm font-medium text-ink flex items-center justify-center"
                                    >
                                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                      </svg>
                                      {getEventConnections(event.id).length} {getEventConnections(event.id).length === 1 ? t('common.connection') : t('common.connections')}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              </div>
            </div>
          </div>

          {/* Edit Event Popup */}
          {showEditEventPopup && selectedEvent && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 50, position: 'fixed', pointerEvents: 'auto' }}>
              <div className="bg-surface p-6 rounded-lg shadow-lg w-96" style={{ position: 'relative' }}>
                <h3 className="text-lg font-semibold mb-4">{t('trajectory.editEvent')}</h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder={t('trajectory.eventName')}
                    value={selectedEvent.name}
                    onChange={(e) => setSelectedEvent({ ...selectedEvent, name: e.target.value })}
                    onKeyDown={(e) =>
                      handleMarkdownShortcut(e, selectedEvent.name, (next) =>
                        setSelectedEvent({ ...selectedEvent, name: next })
                      )
                    }
                    className={formControlClass}
                  />
                  <div>
                    <input
                      type="number"
                      placeholder={t('trajectory.year')}
                      value={selectedEvent.year}
                      onChange={(e) => {
                        const year = parseInt(e.target.value);
                        const error = validateYear(year);
                        setYearError(error);
                        setIsYearValid(!error);
                        setSelectedEvent({ ...selectedEvent, year });
                      }}
                      className={`${formControlClass}${yearError ? ' !border-red-500' : ''}`}
                      min="1900"
                      max={new Date().getFullYear() + 10}
                      step="1"
                    />
                    {yearError && (
                      <p className="text-red-500 text-sm mt-1">{yearError}</p>
                    )}
                  </div>
                  <ShadedSelect
                    value={selectedEvent.type}
                    onChange={(v) => setSelectedEvent({ ...selectedEvent, type: v })}
                    className="w-full"
                    options={eventTypes.map((type) => ({
                      value: type,
                      label: type.charAt(0).toUpperCase() + type.slice(1),
                    }))}
                  />
                  <textarea
                    placeholder={t('trajectory.eventDescription')}
                    value={selectedEvent.description || ''}
                    onChange={(e) => setSelectedEvent({ ...selectedEvent, description: e.target.value })}
                    onKeyDown={(e) =>
                      handleMarkdownShortcut(e, selectedEvent.description || '', (next) =>
                        setSelectedEvent({ ...selectedEvent, description: next })
                      )
                    }
                    className={formControlTextareaClass}
                  />
                  <div className="flex justify-between">
                    <button
                      onClick={() => handleDeleteEvent(selectedEvent.id)}
                      className="px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      {t('common.delete')}
                    </button>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowEditEventPopup(false)}
                        className="px-4 py-2 rounded-full border border-edge-strong bg-surface text-ink hover:bg-surface-muted hover:border-edge"
                      >
                        {hasEventChanged() ? t('common.cancel') : t('common.close')}
                      </button>
                      <button
                        onClick={handleUpdateEvent}
                        disabled={!isYearValid}
                        className={`px-4 py-2 rounded-full ${isYearValid
                          ? 'bg-primary text-primary-foreground hover:bg-primary-hover'
                          : 'bg-surface-muted text-ink-subtle border border-edge cursor-not-allowed'
                          }`}
                      >
                        {t('common.save')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Connection Popup */}
          {showEditConnectionPopup && selectedConnection && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 50, position: 'fixed', pointerEvents: 'auto' }}>
              <div className="bg-surface p-6 rounded-lg shadow-lg w-96" style={{ position: 'relative' }}>
                <h3 className="text-lg font-semibold mb-4">{t('trajectory.editConnection')}</h3>
                {selectedConnection.hasMissingEvents && (
                  <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 rounded text-yellow-700">
                    <p className="text-sm">{t('common.oneOrBothEventsNotFound')}</p>
                  </div>
                )}
                <div className="space-y-4">
                  <ShadedSelect
                    value={selectedConnection.from}
                    onChange={(v) => setSelectedConnection({ ...selectedConnection, from: v })}
                    className="w-full"
                    options={[
                      { value: '', label: t('trajectory.fromEvent') },
                      ...events
                        .filter((event) => event.id !== selectedConnection.to)
                        .map((event) => ({ value: event.id, label: event.name })),
                    ]}
                  />
                  <ShadedSelect
                    value={selectedConnection.to}
                    onChange={(v) => setSelectedConnection({ ...selectedConnection, to: v })}
                    className="w-full"
                    options={[
                      { value: '', label: t('trajectory.toEvent') },
                      ...events
                        .filter((event) => event.id !== selectedConnection.from)
                        .map((event) => ({ value: event.id, label: event.name })),
                    ]}
                  />
                  <ShadedSelect
                    value={selectedConnection.type}
                    onChange={(v) => setSelectedConnection({ ...selectedConnection, type: v })}
                    className="w-full"
                    options={[
                      { value: 'influenced', label: t('trajectory.influenced') },
                      { value: 'led_to', label: t('trajectory.ledTo') },
                      { value: 'related', label: t('trajectory.related') },
                    ]}
                  />
                  <textarea
                    placeholder={t('trajectory.connectionDescription')}
                    value={selectedConnection.description || ''}
                    onChange={(e) => setSelectedConnection({ ...selectedConnection, description: e.target.value })}
                    onKeyDown={(e) =>
                      handleMarkdownShortcut(e, selectedConnection.description || '', (next) =>
                        setSelectedConnection({ ...selectedConnection, description: next })
                      )
                    }
                    className={formControlTextareaClass}
                  />
                  <div className="flex justify-between">
                    <button
                      onClick={() => handleDeleteConnection(selectedConnection.id)}
                      className="px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      {t('common.delete')}
                    </button>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleCloseEditConnectionPopup}
                        className="px-4 py-2 rounded-full border border-edge-strong bg-surface text-ink hover:bg-surface-muted hover:border-edge"
                      >
                        {hasConnectionChanged() ? t('common.cancel') : t('common.close')}
                      </button>
                      <button
                        onClick={handleUpdateConnection}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                      >
                        {t('common.save')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Connections Popup */}
          {showConnectionsPopup && selectedEvent && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 50, position: 'fixed', pointerEvents: 'auto' }}>
              <div className="bg-surface p-6 rounded-lg shadow-lg w-96 max-h-[80vh] flex flex-col" style={{ position: 'relative' }}>
                <h3 className="text-lg font-semibold mb-4">{t('trajectory.connectionsFor', { eventName: selectedEvent.name })}</h3>
                <div className="flex-1 overflow-y-auto mb-4">
                  {selectedEventConnections.length === 0 ? (
                    <p className="text-ink-subtle text-center">{t('common.noConnectionsFound')}</p>
                  ) : (
                    <div className="space-y-3">
                      {selectedEventConnections.map((connection) => {
                        const fromEvent = events.find(e => e.id === connection.from);
                        const toEvent = events.find(e => e.id === connection.to);
                        return (
                          <div
                            key={connection.id}
                            className="p-3 border border-edge rounded-lg bg-surface text-ink hover:bg-surface-muted cursor-pointer"
                            onClick={() => {
                              setSelectedConnection(connection);
                              setShowEditConnectionPopup(true);
                              setShowConnectionsPopup(false);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">
                                  {fromEvent.name} → {toEvent.name}
                                </p>
                                <p className="text-sm text-ink-subtle capitalize">{connection.type}</p>
                                {connection.description && (
                                  <p className="text-sm text-ink-muted mt-1">{connection.description}</p>
                                )}
                              </div>
                              <div className={`w-3 h-3 rounded-full ${getConnectionColor(connection.type)}`} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowConnectionsPopup(false)}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Analysis Popup */}
          {showAnalysisPopup && currentAnalysis && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 50, position: 'fixed', pointerEvents: 'auto' }}>
              <div className="bg-surface p-6 rounded-lg shadow-lg w-3/4 max-h-[80vh] flex flex-col" style={{ position: 'relative' }}>
                <h3 className="text-lg font-semibold mb-4">{t('trajectory.trajectoryAnalysis')}</h3>
                <div className="flex-1 overflow-y-auto mb-4">
                  <div className="prose max-w-none">
                    {renderMarkdown(currentAnalysis.content, {
                      isBot: true,
                      showFullContent: true
                    })}
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  {openedFromPastAnalyses && (
                    <button
                      onClick={() => {
                        setShowAnalysisPopup(false);
                        setShowPastAnalysesPopup(true);
                        setOpenedFromPastAnalyses(false);
                      }}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                    >
                      {t('trajectory.returnToList')}
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      try {
                        setCreatingChat(true);
                        const token = localStorage.getItem('token');
                        if (!token) {
                          throw new Error('No authentication token found');
                        }

                        const chatMessages = getChatMessages();

                        // Create a new chat
                        const chatResponse = await fetch('/api/chat/create', {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            User_ID: currentUser.User_ID,
                            initial_message: chatMessages.initialMessage
                          })
                        });

                        if (!chatResponse.ok) {
                          throw new Error('Failed to create chat');
                        }

                        const chatData = await chatResponse.json();

                        // Generate initial response from chatbot
                        const messageResponse = await fetch(`/api/chat/${chatData.Chat_ID}/message`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            Text: chatMessages.responsePrompt,
                            reference_analysis_id: currentAnalysis.id
                          })
                        });

                        if (!messageResponse.ok) {
                          throw new Error('Failed to get chatbot response');
                        }

                        navigate('/eddy', { state: { chatId: chatData.Chat_ID } });
                      } catch (error) {
                        console.error('Error creating chat:', error);
                        alert('Failed to start chat. Please try again.');
                      } finally {
                        setCreatingChat(false);
                      }
                    }}
                    className="w-full sm:w-auto bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    disabled={isCreatingChat}
                  >
                    {isCreatingChat ? 'Creating chat...' : t('trajectory.askEddy')}
                  </button>
                  <button
                    onClick={() => {
                      setShowAnalysisPopup(false);
                      setOpenedFromPastAnalyses(false);
                    }}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Past Analyses Popup */}
          {showPastAnalysesPopup && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 50, position: 'fixed', pointerEvents: 'auto' }}>
              <div className="bg-surface p-6 rounded-lg shadow-lg w-3/4 max-h-[80vh] flex flex-col" style={{ position: 'relative' }}>
                <h3 className="text-lg font-semibold mb-4">{t('trajectory.pastAnalyses')}</h3>
                <div className="flex-1 overflow-y-auto mb-4">
                  {pastAnalyses.length === 0 ? (
                    <p className="text-ink-subtle text-center">{t('common.noPastAnalysesAvailable')}</p>
                  ) : (
                    <div className="space-y-4">
                      {pastAnalyses.map(analysis => (
                        <div
                          key={analysis.id}
                          className="p-4 border border-edge rounded-lg bg-surface text-ink hover:bg-surface-muted"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <p className="font-medium text-ink-muted">
                              {t('trajectory.analysisFrom', { date: new Date(analysis.timestamp).toLocaleString() })}
                            </p>
                            <button
                              onClick={() => handleDeleteAnalysis(analysis.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                              title={t('common.deleteAnalysis')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          <div
                            className="prose max-w-none cursor-pointer"
                            onClick={() => {
                              setCurrentAnalysis(analysis);
                              setShowAnalysisPopup(true);
                              setShowPastAnalysesPopup(false);
                              setOpenedFromPastAnalyses(true);
                            }}
                          >
                            {renderMarkdown(analysis.content, {
                              isBot: true,
                              charLimit: 500
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowPastAnalysesPopup(false)}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bottom panel for mobile */}
          {screenWidth < 1024 && (
            <div
              className="fixed bottom-0 left-0 right-0 bg-surface border-t border-edge transition-all duration-300 shadow-lg"
              style={{
                height: `${panelHeight}px`,
                transform: `translateY(${isBottomPanelOpen ? 0 : panelHeight - 40}px)`,
                transition: dragStartY === null ? 'transform 0.3s ease-out' : 'none',
                zIndex: 30
              }}
            >
              {/* Drag handle */}
              <div
                className="h-8 flex items-center justify-center cursor-grab active:cursor-grabbing bg-canvas touch-none select-none drag-handle"
                onMouseDown={handleDragStart}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ touchAction: 'none' }}
              >
                <div className="w-12 h-1 bg-edge rounded-full"></div>
              </div>

              {/* Panel content */}
              <div className="overflow-y-auto h-[calc(100%-32px)] p-4">
                {isBottomPanelOpen ? (
                  <>
                    {/* Full control panel content */}
                    <div className="space-y-6">
                      {/* Add Event Section */}
                      <div>
                        <div className="flex justify-between items-center mb-4 gap-2">
                          <h2 className="text-lg font-semibold">{t('trajectory.addEvent')}</h2>
                          <div className="flex items-center gap-2 shrink-0">
                          <TutorialInlineButton
                            onClick={openGuide}
                            unstyled
                            className="px-2.5 py-1.5 text-xs font-semibold rounded-full border border-edge-strong text-ink-muted bg-surface hover:bg-surface-muted hover:text-link-hover hover:border-link/40 transition-colors"
                          />
                          <button
                            onClick={() => {
                              setShowManageTypesPopup(true);
                              setIsBottomPanelOpen(false);
                            }}
                            className="text-sm text-ink-subtle hover:text-ink"
                            title={t('common.manageEventTypes')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <input
                            type="text"
                            placeholder={t('trajectory.eventName')}
                            value={newEvent.name}
                            onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                            onKeyDown={(e) =>
                              handleMarkdownShortcut(e, newEvent.name, (next) =>
                                setNewEvent({ ...newEvent, name: next })
                              )
                            }
                            className={formControlClass}
                          />
                          <div>
                            <input
                              type="number"
                              placeholder={t('trajectory.year')}
                              value={newEvent.year}
                              onChange={handleYearChange}
                              className={`${formControlClass}${yearError ? ' !border-red-500' : ''}`}
                              min="1900"
                              max={new Date().getFullYear() + 10}
                              step="1"
                            />
                            {yearError && (
                              <p className="text-red-500 text-sm mt-1">{yearError}</p>
                            )}
                          </div>
                          <ShadedSelect
                            value={newEvent.type}
                            onChange={(v) => handleEventTypeChange({ target: { value: v } })}
                            className="w-full"
                            options={[
                              { value: '', label: t('trajectory.selectEventType') },
                              ...eventTypes.map((type) => ({
                                value: type,
                                label: type.charAt(0).toUpperCase() + type.slice(1),
                              })),
                              { value: 'custom', label: t('common.addNew') },
                            ]}
                          />
                          <textarea
                            placeholder={t('common.eventDescription')}
                            value={newEvent.description || ''}
                            onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                            onKeyDown={(e) =>
                              handleMarkdownShortcut(e, newEvent.description || '', (next) =>
                                setNewEvent({ ...newEvent, description: next })
                              )
                            }
                            className={formControlTextareaClass}
                          />
                          <button
                            onClick={handleAddEvent}
                            disabled={!isEventValid}
                            className={`w-full p-2 rounded-full ${isEventValid
                              ? 'bg-primary text-primary-foreground hover:bg-primary-hover'
                              : 'bg-surface-muted text-ink-subtle border border-edge cursor-not-allowed'
                              }`}
                          >
                            {t('trajectory.addEvent')}
                          </button>
                        </div>
                      </div>

                      {/* Add Connection Section */}
                      <div>
                        <h2 className="text-lg font-semibold mb-4">{t('common.addConnection')}</h2>
                        <div className="space-y-4">
                          <ShadedSelect
                            value={newConnection.from}
                            onChange={(v) => setNewConnection({ ...newConnection, from: v })}
                            className="w-full"
                            options={[
                              { value: '', label: t('trajectory.fromEvent') },
                              ...events
                                .filter((event) => event.id !== newConnection.to)
                                .map((event) => ({ value: event.id, label: event.name })),
                            ]}
                          />
                          <ShadedSelect
                            value={newConnection.to}
                            onChange={(v) => setNewConnection({ ...newConnection, to: v })}
                            className="w-full"
                            options={[
                              { value: '', label: t('trajectory.toEvent') },
                              ...events
                                .filter((event) => event.id !== newConnection.from)
                                .map((event) => ({ value: event.id, label: event.name })),
                            ]}
                          />
                          <ShadedSelect
                            value={newConnection.type}
                            onChange={(v) => setNewConnection({ ...newConnection, type: v })}
                            className="w-full"
                            options={[
                              { value: '', label: t('trajectory.selectConnectionType') },
                              { value: 'influenced', label: t('trajectory.influenced') },
                              { value: 'led_to', label: t('trajectory.ledTo') },
                              { value: 'related', label: t('trajectory.related') },
                            ]}
                          />
                          <textarea
                            placeholder={t('common.connectionDescription')}
                            value={newConnection.description || ''}
                            onChange={(e) => setNewConnection({ ...newConnection, description: e.target.value })}
                            onKeyDown={(e) =>
                              handleMarkdownShortcut(e, newConnection.description || '', (next) =>
                                setNewConnection({ ...newConnection, description: next })
                              )
                            }
                            className={formControlTextareaClass}
                          />
                          <button
                            onClick={handleAddConnection}
                            disabled={!isConnectionValid}
                            className={`w-full p-2 rounded-full ${isConnectionValid
                              ? 'bg-primary text-primary-foreground hover:bg-primary-hover'
                              : 'bg-surface-muted text-ink-subtle border border-edge cursor-not-allowed'
                              }`}
                          >
                            {t('common.addConnection')}
                          </button>
                        </div>
                      </div>

                      {/* Analysis buttons */}
                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            handleAnalyzeTrajectory();
                            setIsBottomPanelOpen(false);
                          }}
                          disabled={isAnalyzing}
                          className={`w-full p-2 rounded-full ${isAnalyzing
                            ? 'bg-surface-muted text-ink-subtle border border-edge cursor-not-allowed'
                            : 'bg-primary text-primary-foreground hover:bg-primary-hover'
                            }`}
                        >
                          {isAnalyzing ? t('common.loading') : t('trajectory.trajectoryAnalysis')}
                        </button>
                        <button
                          onClick={() => {
                            setShowPastAnalysesPopup(true);
                            setIsBottomPanelOpen(false);
                          }}
                          className="w-full p-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                        >
                          {t('trajectory.pastAnalyses')}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-center space-x-2">
                    <button
                      onClick={() => {
                        handleAnalyzeTrajectory();
                        setIsBottomPanelOpen(false);
                      }}
                      disabled={isAnalyzing}
                      className={`flex-1 p-2 rounded-full ${isAnalyzing
                        ? 'bg-surface-muted text-ink-subtle border border-edge cursor-not-allowed'
                        : 'bg-primary text-primary-foreground hover:bg-primary-hover'
                        }`}
                    >
                      {isAnalyzing ? t('common.loading') : 'Analyze'}
                    </button>
                    <button
                      onClick={() => {
                        setShowPastAnalysesPopup(true);
                        setIsBottomPanelOpen(false);
                      }}
                      className="flex-1 p-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                    >
                      {t('trajectory.pastAnalyses')}
                    </button>
                    <button
                      onClick={() => {
                        setShowManageTypesPopup(true);
                        setIsBottomPanelOpen(false);
                      }}
                      className="flex-1 p-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                      title={t('common.manageEventTypes')}
                    >
                      <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Custom Type Popup */}
          {showCustomTypePopup && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 50, position: 'fixed', pointerEvents: 'auto' }}>
              <div className="bg-surface p-6 rounded-lg shadow-lg w-96" style={{ position: 'relative' }}>
                <h3 className="text-lg font-semibold mb-4">{t('common.addNewEventType')}</h3>
                <input
                  type="text"
                  placeholder={t('common.enterNewEventType')}
                  value={customTypeInput}
                  onChange={(e) => setCustomTypeInput(e.target.value)}
                  className={`${formControlClass} mb-4`}
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setShowCustomTypePopup(false);
                      setCustomTypeInput('');
                      setIsBottomPanelOpen(true);
                    }}
                    className="px-4 py-2 rounded-full border border-edge-strong bg-surface text-ink hover:bg-surface-muted hover:border-edge"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleCustomTypeSubmit();
                      setIsBottomPanelOpen(true);
                    }}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Manage Types Popup */}
          {showManageTypesPopup && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 50, position: 'fixed', pointerEvents: 'auto' }}>
              <div className="bg-surface p-6 rounded-lg shadow-lg w-96" style={{ position: 'relative' }}>
                <h3 className="text-lg font-semibold mb-4">{t('common.manageEventTypes')}</h3>
                <div className="space-y-2 mb-4">
                  {eventTypes.map(type => {
                    const isDragged = type === draggedType;
                    const isDragOver = type === dragOverType;
                    const draggedIndex = eventTypes.indexOf(draggedType);
                    const currentIndex = eventTypes.indexOf(type);

                    // Calculate shift direction only when actively dragging over
                    const shouldShift = dragOverType && !isDragged && !isDragOver;
                    const shiftDirection = shouldShift ?
                      (currentIndex > draggedIndex && currentIndex <= eventTypes.indexOf(dragOverType) ? -1 :
                        currentIndex < draggedIndex && currentIndex >= eventTypes.indexOf(dragOverType) ? 1 : 0) : 0;

                    return (
                      <div
                        key={type}
                        draggable
                        onDragStart={(e) => handleTypeDragStart(e, type)}
                        onDragOver={(e) => handleTypeDragOver(e, type)}
                        onDragLeave={(e) => handleTypeDragLeave(e, type)}
                        onDrop={(e) => handleTypeDrop(e, type)}
                        className={`flex items-center justify-between p-2 rounded cursor-move transition-all duration-200 ${isDragged ? 'opacity-50 bg-surface-muted' :
                          isDragOver ? 'bg-accent/25' :
                            'bg-canvas hover:bg-surface-muted'
                          }`}
                        style={{
                          transform: isDragged ? 'scale(1.05)' :
                            shouldShift ? `translateY(${shiftDirection * 40}px)` : 'none',
                          transition: 'transform 0.5s ease-out, background-color 0.5s ease-out'
                        }}
                      >
                        <span className="capitalize">{type}</span>
                        <button
                          onClick={() => handleDeleteType(type)}
                          className="text-red-500 hover:text-red-700"
                          title={t('common.deleteType')}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setShowManageTypesPopup(false);
                      setIsBottomPanelOpen(true);
                    }}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}
          {isCreatingChat && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-surface p-8 rounded-lg shadow-xl max-w-md w-full mx-4">
                <div className="flex flex-col items-center">
                  <LoadingSpinner size="large" className="mb-4" />
                  <h3 className="text-xl font-semibold text-ink mb-2">Creating Your Chat</h3>
                  <p className="text-ink-muted text-center">Please wait while we set up your conversation with Eddy...</p>
                </div>
              </div>
            </div>
          )}

          <PageTutorialGuide
            active={guideOpen}
            onClose={closeGuide}
            t={t}
            steps={trajectoryGuideSteps}
            anchorRefs={trajectoryTutorialAnchors}
          />
        </>
      )}
    </div>
  );
}

export default TrajectoryMap; 
